// Prize System Service
// Shared prize module for engagement games (lottery, roulette, prediction, dance-off, trivia, awards)

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';

export type PrizeType = 'pick_next_song' | 'auto_queue_bypass' | 'custom' | 'shoutout';
export type PrizeStatus = 'configured' | 'awarded' | 'claimed' | 'expired' | 'revoked';
export type TieBreakMethod = 'random' | 'dj_picks' | 'all_win';

export interface Prize {
  id: string;
  eventId: string;
  djId: string;
  type: PrizeType;
  customText?: string;
  winnerId?: string;
  winnerNickname?: string;
  status: PrizeStatus;
  gameSource?: string;
  gameRoundId?: string;
  tieBreakMethod?: TieBreakMethod;
  placement?: number;
  timeoutMinutes: number;
  awardedAt?: string;
  claimedAt?: string;
  expiresAt?: string;
  revokedAt?: string;
  repickReason?: 'co-approver-rejected' | 'dj-rejected';
  claimData?: {
    songTitle?: string;
    songArtist?: string;
    albumArtUrl?: string;
    requestId?: string;
  };
  createdAt: string;
  updatedAt: string;
}

function docToPrize(docSnap: any): Prize {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    eventId: data.eventId,
    djId: data.djId,
    type: data.type,
    customText: data.customText || undefined,
    winnerId: data.winnerId || undefined,
    winnerNickname: data.winnerNickname || undefined,
    status: data.status,
    gameSource: data.gameSource || undefined,
    gameRoundId: data.gameRoundId || undefined,
    tieBreakMethod: data.tieBreakMethod || undefined,
    placement: data.placement || undefined,
    timeoutMinutes: data.timeoutMinutes ?? 5,
    awardedAt: data.awardedAt || undefined,
    claimedAt: data.claimedAt || undefined,
    expiresAt: data.expiresAt || undefined,
    revokedAt: data.revokedAt || undefined,
    repickReason: data.repickReason || undefined,
    claimData: data.claimData || undefined,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}

export function generatePrizeDocId(
  eventId: string,
  gameSource: string,
  gameRoundId: string,
  placement: number
): string {
  return `${eventId}_${gameSource}_${gameRoundId}_${placement}`;
}

export function computeExpiresAt(awardedAt: string, timeoutMinutes: number): string {
  const awarded = new Date(awardedAt);
  awarded.setMinutes(awarded.getMinutes() + timeoutMinutes);
  return awarded.toISOString();
}

export async function createPrize(
  eventId: string,
  djId: string,
  type: PrizeType,
  options: {
    customText?: string;
    timeoutMinutes?: number;
    gameSource?: string;
    gameRoundId?: string;
    tieBreakMethod?: TieBreakMethod;
    placement?: number;
  } = {}
): Promise<string> {
  const now = new Date().toISOString();
  const docRef = await addDoc(collection(db, 'prizes'), {
    eventId,
    djId,
    type,
    customText: options.customText || null,
    status: 'configured',
    gameSource: options.gameSource || 'manual',
    gameRoundId: options.gameRoundId || null,
    tieBreakMethod: options.tieBreakMethod || null,
    placement: options.placement || 1,
    timeoutMinutes: options.timeoutMinutes ?? 5,
    createdAt: now,
    updatedAt: now,
  });
  return docRef.id;
}

export async function awardPrize(
  prizeId: string,
  winnerId: string,
  winnerNickname?: string
): Promise<void> {
  const now = new Date().toISOString();
  const prizeRef = doc(db, 'prizes', prizeId);

  const prizeDocs = await getDocs(query(collection(db, 'prizes'), where('__name__', '==', prizeId)));
  const prizeData = prizeDocs.docs[0]?.data();
  const timeoutMinutes = prizeData?.timeoutMinutes ?? 5;

  const expiresAt = computeExpiresAt(now, timeoutMinutes);
  await updateDoc(prizeRef, {
    winnerId,
    winnerNickname: winnerNickname || null,
    status: 'awarded',
    awardedAt: now,
    expiresAt,
    updatedAt: now,
  });
}

export async function claimPrize(
  prizeId: string,
  claimData?: Prize['claimData']
): Promise<void> {
  const now = new Date().toISOString();
  await updateDoc(doc(db, 'prizes', prizeId), {
    status: 'claimed',
    claimedAt: now,
    ...(claimData ? { claimData } : {}),
    updatedAt: now,
  });
}

export async function revokePrize(prizeId: string): Promise<void> {
  const now = new Date().toISOString();
  await updateDoc(doc(db, 'prizes', prizeId), {
    status: 'revoked',
    revokedAt: now,
    updatedAt: now,
  });
}

export async function reassignPrize(
  prizeId: string,
  newWinnerId: string,
  newNickname?: string
): Promise<void> {
  const now = new Date().toISOString();

  const prizeDocs = await getDocs(query(collection(db, 'prizes'), where('__name__', '==', prizeId)));
  const prizeData = prizeDocs.docs[0]?.data();
  const timeoutMinutes = prizeData?.timeoutMinutes ?? 5;
  const computedExpiresAt = computeExpiresAt(now, timeoutMinutes);

  await updateDoc(doc(db, 'prizes', prizeId), {
    winnerId: newWinnerId,
    winnerNickname: newNickname || null,
    status: 'awarded',
    awardedAt: now,
    expiresAt: computedExpiresAt,
    claimedAt: null,
    claimData: null,
    revokedAt: null,
    updatedAt: now,
  });
}

export async function getActivePrizesForEvent(eventId: string): Promise<Prize[]> {
  const q = query(
    collection(db, 'prizes'),
    where('eventId', '==', eventId)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map(docToPrize)
    .filter(p => p.status !== 'revoked');
}

export async function getUnclaimedPrizesForAttendee(
  eventId: string,
  attendeeId: string
): Promise<Prize[]> {
  const q = query(
    collection(db, 'prizes'),
    where('eventId', '==', eventId),
    where('winnerId', '==', attendeeId),
    where('status', '==', 'awarded')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(docToPrize);
}

export function subscribeToAttendeePrizes(
  eventId: string,
  attendeeId: string,
  callback: (prizes: Prize[]) => void
): Unsubscribe {
  const q = query(
    collection(db, 'prizes'),
    where('eventId', '==', eventId),
    where('winnerId', '==', attendeeId)
  );
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(docToPrize));
  });
}

export function subscribeToDJPrizes(
  eventId: string,
  callback: (prizes: Prize[]) => void
): Unsubscribe {
  const q = query(
    collection(db, 'prizes'),
    where('eventId', '==', eventId)
  );
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(docToPrize));
  });
}

export async function checkExpiredPrizes(eventId: string): Promise<number> {
  const now = new Date().toISOString();
  const q = query(
    collection(db, 'prizes'),
    where('eventId', '==', eventId),
    where('status', '==', 'awarded')
  );
  const snapshot = await getDocs(q);
  let expiredCount = 0;

  for (const prizeDoc of snapshot.docs) {
    const data = prizeDoc.data();
    if (data.expiresAt && data.expiresAt < now) {
      await updateDoc(prizeDoc.ref, {
        status: 'expired',
        updatedAt: now,
      });
      expiredCount++;
    }
  }

  return expiredCount;
}
